import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IndianRupee } from "lucide-react";
import { Callout, Checkbox, FieldGroup, FormField, Input, Radio } from "../ui";
import { formatCurrency } from "../../utils/format";
import { previewCoursePayments } from "../../utils/websiteCheckout";
import { getWebsiteCheckoutSettings } from "../../utils/websiteOrdersApi";

/**
 * CoursePaymentFields — a batch course's registration fee, and whether
 * visitors can pay for the course on the institute's website.
 *
 * Both are saved with the course form (so they are checkboxes, not switches).
 * The preview is utils/websiteCheckout — vc_be's rules — so what it says the
 * website will offer is what the server will charge.
 *
 * Props:
 *   fees               — the course fee as typed in the form (free text)
 *   registrationFee    — { enabled, amount, includedInFee }
 *   websiteCheckout    — { enabled }
 *   showPricePublicly  — the form's "Show the fee on your website"
 *   hasWebsite         — the plan includes the public website
 *   onChange           — (patch) => void, with `registrationFee` / `websiteCheckout`
 */

/**
 * One setting as a bordered panel: the checkbox on top, and what it opens in a
 * tinted body underneath while it is ticked. Borders are written for all four
 * sides — Preflight is off, so a lone `tw-border-t` + `tw-border-solid` would
 * draw the browser's 3px default on the other three.
 */
const SettingPanel = ({ on, control, children }) => (
  <div
    className={`tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-bg-surface-raised tw-transition-colors ${on ? "tw-border-primary/40" : "tw-border-line"}`}
  >
    <div className="tw-px-4 tw-py-3.5">{control}</div>
    {on && children && (
      <div className="tw-flex tw-flex-col tw-gap-4 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-line tw-bg-surface-alt tw-px-4 tw-py-4">
        {children}
      </div>
    )}
  </div>
);

/** One way a visitor can pay, as the website will offer it. */
const OptionTile = ({ title, amount, note }) => (
  <div className="tw-flex tw-flex-col tw-gap-0.5 tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-px-3.5 tw-py-3">
    <span className="tw-text-[0.8125rem] tw-text-content-muted">{title}</span>
    <span className="tw-text-lg tw-font-semibold tw-leading-tight tw-tabular-nums tw-text-content">{formatCurrency(amount)}</span>
    <span className="tw-text-xs tw-text-content-subtle">{note}</span>
  </div>
);

const CoursePaymentFields = ({
  fees,
  registrationFee,
  websiteCheckout,
  showPricePublicly = true,
  hasWebsite = false,
  onChange,
}) => {
  const reg = registrationFee || { enabled: false, amount: "", includedInFee: true };
  const web = websiteCheckout || { enabled: false };
  const preview = previewCoursePayments({ fees, registrationFee: reg });
  const regError = preview.field === "registrationFee.amount" || preview.field === "fees" ? preview.error : "";
  const [institute, setInstitute] = useState(null);

  // Whether the institute's own switch is on — only to say so here. A staff
  // member without the payments setting gets a 403, and simply no note.
  useEffect(() => {
    if (!hasWebsite) return undefined;
    let live = true;
    getWebsiteCheckoutSettings()
      .then(({ data }) => { if (live) setInstitute(data.data); })
      .catch(() => {});
    return () => { live = false; };
  }, [hasWebsite]);

  const setReg = (patch) => onChange({ registrationFee: { ...reg, ...patch } });
  const fee = preview.fee;
  const regAmount = Number(String(reg.amount ?? "").replace(/[,\s₹]/g, "")) || 0;
  const included = reg.includedInFee !== false;
  const showTotal = !preview.error && preview.total != null && regAmount > 0;

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <SettingPanel
        on={!!reg.enabled}
        control={(
          <Checkbox
            checked={!!reg.enabled}
            onChange={(e) => setReg({ enabled: e.target.checked })}
            label="Charge a registration fee"
            description="A fixed amount a student pays to secure their place. On your website they can pay just this first, and the rest later."
          />
        )}
      >
        <div className="tw-grid tw-grid-cols-1 tw-items-start tw-gap-3 sm:tw-grid-cols-2">
          <FormField label="Registration fee" required error={regError}>
            <Input
              inputMode="decimal"
              autoComplete="off"
              leading={<IndianRupee size={14} aria-hidden="true" />}
              value={reg.amount ?? ""}
              onChange={(e) => setReg({ amount: e.target.value })}
              placeholder="e.g. 2000"
            />
          </FormField>

          {showTotal && (
            <div className="tw-flex tw-flex-col tw-gap-0.5 tw-rounded-md tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-px-3.5 tw-py-2.5">
              <span className="tw-text-[0.8125rem] tw-text-content-muted">Each enrolment costs</span>
              <span className="tw-text-lg tw-font-semibold tw-leading-tight tw-tabular-nums tw-text-content">{formatCurrency(preview.total)}</span>
              <span className="tw-text-xs tw-tabular-nums tw-text-content-subtle">
                {included
                  ? `Includes the ${formatCurrency(regAmount)} registration fee`
                  : `${formatCurrency(fee)} course fee + ${formatCurrency(regAmount)} registration fee`}
              </span>
            </div>
          )}
        </div>

        <FieldGroup legend="Is it part of the course fee?">
          <div className="tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-2">
            <Radio
              variant="card"
              name="registrationIncluded"
              checked={included}
              onChange={() => setReg({ includedInFee: true })}
              label="Yes — part of the course fee"
              description={fee && regAmount > 0
                ? `${formatCurrency(regAmount)} of the ${formatCurrency(fee)} is paid first.`
                : "Paid first, out of the course fee."}
            />
            <Radio
              variant="card"
              name="registrationIncluded"
              checked={!included}
              onChange={() => setReg({ includedInFee: false })}
              label="No — charged on top"
              description={fee && regAmount > 0
                ? `Added to the ${formatCurrency(fee)} course fee.`
                : "Added to the course fee."}
            />
          </div>
        </FieldGroup>

        {!included && showTotal && (
          <p className="tw-m-0 tw-text-xs tw-leading-relaxed tw-text-content-muted">
            New enrolments — from your website, the student form or self-enrolment — are priced at {formatCurrency(preview.total)}.
            Students already enrolled keep the fee they have.
          </p>
        )}
      </SettingPanel>

      {hasWebsite && (
        <SettingPanel
          on={!!web.enabled}
          control={(
            <Checkbox
              checked={!!web.enabled}
              onChange={(e) => onChange({ websiteCheckout: { enabled: e.target.checked } })}
              label="Take payments on your website"
              description="Visitors pay on this course's page and are enrolled straight away, with a receipt and their login details by email."
            />
          )}
        >
          {!preview.error && preview.options.length > 0 && (
            <div className="tw-flex tw-flex-col tw-gap-2">
              <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle">
                Visitors can choose
              </span>
              <div className="tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-2">
                {preview.options.map((o) => (
                  <OptionTile
                    key={o.key}
                    title={o.key === "registration" ? "Registration fee now" : "Pay in full"}
                    amount={o.amount}
                    note={o.balance > 0 ? `${formatCurrency(o.balance)} paid later` : "Nothing due later"}
                  />
                ))}
              </div>
              <span className="tw-text-xs tw-text-content-subtle">They can still send an enquiry instead.</span>
            </div>
          )}

          {!preview.error && preview.options.length === 0 && (
            <Callout tone="warning">Enter a course fee — a course with no fee cannot be paid for online.</Callout>
          )}
          {preview.error && (
            <Callout tone="warning">Fix the registration fee above to see what visitors will pay.</Callout>
          )}
          {showPricePublicly === false && (
            <Callout tone="warning">
              The fee is hidden on your website, so the Pay button will not show. Tick “Show the fee on your website”.
            </Callout>
          )}
          {institute && !institute.enabled && (
            <Callout tone="warning">
              Website payments are switched off for your institute.{" "}
              <Link to="/settings?tab=payments" className="tw-font-semibold tw-text-primary">Turn them on in Settings → Payments</Link>.
            </Callout>
          )}
        </SettingPanel>
      )}
    </div>
  );
};

export default CoursePaymentFields;
